// src/components/admin/nornsweave/PendingTab.js
import React from 'react';
import { useAdminNornsWeave, DAY_OPTIONS } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';

function PendingTab({
  onApproveBooking,
  onDeclineBooking,
  onCreateFixedClass,
  getDayName
}) {
  const { pendingBookings } = useAdminNornsWeave();

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Pending Booking Approvals</h2>
          <p className="text-slate-600 text-sm mt-1">Review and approve teacher booking requests</p>
        </div>
        {pendingBookings.length > 0 && (
          <div className="bg-orange-100 border border-orange-300 rounded-lg px-4 py-2">
            <p className="text-orange-800 font-bold text-lg">{pendingBookings.length} Pending</p>
          </div>
        )}
      </div>

      {pendingBookings.length === 0 ? (
        <div className="text-center py-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
          <div className="text-6xl mb-4">✅</div>
          <p className="text-slate-600 text-lg font-medium">All caught up!</p>
          <p className="text-slate-500 text-sm mt-2">No pending booking requests to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingBookings.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((booking, idx) => (
            <div key={booking.id} className="border-2 border-orange-300 rounded-xl p-6 bg-gradient-to-r from-orange-50 to-yellow-50 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                      New Request #{idx + 1}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      booking.type === 'admin' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                    }`}>
                      {booking.type === 'admin' ? 'Admin' : 'Class'}
                    </span>
                    {booking.needsLibrarian && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500 text-white">
                        Librarian Needed
                      </span>
                    )}
                    {booking.isRecurring && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-500 text-white">
                        Recurring Request
                      </span>
                    )}
                    <span className="text-xs text-slate-500">
                      Submitted: {new Date(booking.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          booking.library === 'elementary' ? 'bg-purple-200 text-purple-800' : 'bg-indigo-200 text-indigo-800'
                        }`}>
                          {booking.library === 'elementary' ? 'Elementary' : 'Middle School'}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h4 className="font-bold text-slate-700 mb-2">Teacher Info:</h4>
                        <p className="text-sm"><span className="text-slate-500">Name:</span> <span className="font-medium">{booking.teacherName}</span></p>
                        <p className="text-sm"><span className="text-slate-500">Email:</span> <span className="font-medium">{booking.teacherEmail}</span></p>
                        {booking.grade && <p className="text-sm"><span className="text-slate-500">Grade:</span> <span className="font-medium">{booking.grade}</span></p>}
                        {booking.studentCount && <p className="text-sm"><span className="text-slate-500">Students:</span> <span className="font-medium">{booking.studentCount}</span></p>}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h4 className="font-bold text-slate-700 mb-2">Visit Details:</h4>
                        <p className="text-sm"><span className="text-slate-500">Date:</span> <span className="font-medium">{new Date(booking.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></p>
                        <p className="text-sm"><span className="text-slate-500">Time:</span> <span className="font-medium">{formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}</span></p>
                        <p className="text-sm"><span className="text-slate-500">Purpose:</span> <span className="font-medium">{booking.purpose}</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Services Requested */}
                  {booking.services && Object.values(booking.services).some(v => v === true) && (
                    <div className="mt-3 bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-bold text-slate-700 mb-2">Services Requested:</h4>
                      <div className="flex flex-wrap gap-2">
                        {booking.services.researchSupport && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Research Support</span>}
                        {booking.services.bookCheckout && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Book Checkout</span>}
                        {booking.services.projectorScreen && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Projector/Screen</span>}
                        {booking.services.bookSetsNeeded && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Book Sets: {booking.services.bookSetsDetails}</span>}
                        {booking.services.aideHelpRequested && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Aide Help: {booking.services.aideHelpDetails}</span>}
                        {booking.services.other && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Other: {booking.services.otherDetails}</span>}
                      </div>
                    </div>
                  )}

                  {/* Lesson Request */}
                  {booking.lessonRequest && (
                    <div className="mt-3 bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="font-bold text-purple-700 mb-1 text-sm">Lesson Request:</h4>
                      <p className="text-sm text-slate-700">{booking.lessonRequest}</p>
                    </div>
                  )}

                  {booking.notes && (
                    <div className="mt-3 bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-bold text-slate-700 mb-1 text-sm">Additional Notes:</h4>
                      <p className="text-sm text-slate-600 italic">"{booking.notes}"</p>
                    </div>
                  )}

                  {/* Recurring Request Details */}
                  {booking.isRecurring && (
                    <div className="mt-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border-2 border-cyan-200">
                      <h4 className="font-bold text-cyan-700 mb-2 flex items-center gap-2">
                        Recurring Weekly Request
                      </h4>
                      <p className="text-sm text-slate-700">
                        Teacher is requesting <strong>every {booking.recurringDay}</strong> at {formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}
                        {booking.recurringEndDate && (
                          <span className="text-slate-500"> until {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        )}
                      </p>
                      <p className="text-xs text-cyan-600 mt-2">
                        Consider approving as a one-time booking or creating a fixed recurring class.
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => onApproveBooking(booking)}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all font-bold shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <span>✓</span> Approve
                  </button>
                  {booking.isRecurring && (
                    <button
                      onClick={() => onCreateFixedClass(booking, getDayName)}
                      className="bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-all font-bold shadow-md hover:shadow-lg flex items-center gap-2"
                    >
                      <span>📌</span> Fixed Class
                    </button>
                  )}
                  <button
                    onClick={() => onDeclineBooking(booking)}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-all font-bold shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <span>✗</span> Decline
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PendingTab;
